import { type FC, useContext, useEffect } from "react";

import { useQuery } from "@apollo/client";
import { useAtom, useSetAtom } from "jotai";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId, type ResourceContainer } from "../../gql-generated/types";
import { UserInfoContext } from "../../shared/api/contexts";
import { type MyLensesOrganizationsQuery, myLensesOrganizations } from "../../shared/api/queries";

import {
    defaultOrganizationIdV2Atom,
    organizationsV2DataAtom,
    organizationsV2ErrorAtom,
    organizationsV2InitializedAtom,
    organizationsV2LoadingAtom,
    organizationsV2RefetchAtom,
} from "./organizationsV2";

export const OrganizationsV2StateInitializer: FC = () => {
    const { configs, loading: featureGatesLoading } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;

    const setOrganizations = useSetAtom(organizationsV2DataAtom);
    const setDefaultOrganizationId = useSetAtom(defaultOrganizationIdV2Atom);
    const setLoading = useSetAtom(organizationsV2LoadingAtom);
    const setError = useSetAtom(organizationsV2ErrorAtom);
    const [refetchCounter] = useAtom(organizationsV2RefetchAtom);
    const setInitialized = useSetAtom(organizationsV2InitializedAtom);

    const userInfo = useContext(UserInfoContext);

    const {
        loading: allOrgsLoading,
        error: apolloError,
        data,
        refetch: refetchAllOrgs,
    } = useQuery<MyLensesOrganizationsQuery>(myLensesOrganizations, {
        variables: {
            limit: 0,
            cursor: "",
        },
        skip: !isMyLensesOrgsV2Enabled,
    });

    useEffect(() => {
        const refetchFn = async () => {
            if (refetchCounter > 0) {
                await refetchAllOrgs();
            }
        };
        void refetchFn();
    }, [refetchCounter, refetchAllOrgs]);

    useEffect(() => {
        if (!userInfo?.data) {
            setLoading(featureGatesLoading || allOrgsLoading);
            return;
        }

        const {
            getUserProfile: { defaultOrganizationId },
        } = userInfo.data;

        const organizations: ResourceContainer[] = [...(data?.myLensesOrganizations.organizationList ?? [])];

        setOrganizations(organizations);
        setDefaultOrganizationId(defaultOrganizationId);
        setLoading(featureGatesLoading || allOrgsLoading);
        setError(apolloError);

        if (!featureGatesLoading && !allOrgsLoading) {
            setInitialized(true);
        }
    }, [
        userInfo,
        data,
        allOrgsLoading,
        apolloError,
        featureGatesLoading,
        setOrganizations,
        setDefaultOrganizationId,
        setLoading,
        setError,
        setInitialized,
    ]);

    return null;
};
